/**
 * **Options and add-ons on the catalog** — `docs/catalog-options-design.md`.
 *
 * Two facts about one sellable that the catalog could not hold before:
 *
 *  * an **option** is a way the same thing is sold — *"one facial at 30 · 60 ·
 *    90 · 120 for 85 / 170 / 255 / 340"* — so its `price_cents` is **absolute**
 *    and its `duration_min` **replaces** the item's. The owner wrote 85 / 170 /
 *    255 / 340, not 85 and three `+85`s; a delta would invent a base nobody
 *    chose and would break the first time she repriced the 60 alone;
 *  * an **add-on** is a different thing, sold *attached* — *"Dermaplaning / +add
 *    to facial — 85/45"* — so it has its own id, its own settlement line and its
 *    own report row, and a **different price when attached**. Its minutes
 *    **add**.
 *
 * Minutes add; the option replaces. That one sentence is the whole arithmetic,
 * and it is stated here so both sides of the wire can be tested against the same
 * words:
 *
 * > **span  = effectiveDurationMin(service, party, override, option) + Σ add_on.duration_min**
 * > **price = (option ?? item).price_cents + Σ add_on.price_cents**
 *
 * This file is pure vocabulary and arithmetic. It decides **when** nothing: the
 * span resolver is `effectiveDurationMin` in `@yourstore/scheduling` (dev.md
 * rule 3, one engine), and the I/O is `packages/store`'s.
 */
/**
 * **The default option — the one rule, in the one function** (owner's ruling 1,
 * `docs/plan-2026-09-21.md`: *"the owner marks a default option, else the first
 * in her order"*).
 *
 * The option she marked; failing that the lowest `sort_order`, ties broken by
 * id so two reads of one catalog are the same document. Inactive options are out
 * of the running entirely — an 86'd length is not the headline, and a marked
 * default that has since been deactivated falls through to the first live one
 * rather than pricing the item at something nobody can book.
 *
 * `null` when the item has no live options, which is every item in the fleet
 * today and is what makes this whole change additive.
 *
 * It is exported from `domain` rather than written in the store because three
 * layers need the same answer — the catalog projection, the booking price, and
 * the partner's `GET /services` top-level numbers — and the agreement's
 * additivity rests on all three agreeing (design §4.3 rule 2: *a caller that
 * never learns the word "option" is offered the default's span, is charged the
 * default's price, and is never wrong*).
 */
export function defaultItemOption(options) {
    const live = options.filter((o) => o.active);
    if (live.length === 0)
        return null;
    const marked = live.find((o) => o.is_default);
    if (marked !== undefined)
        return marked;
    return ([...live].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id))[0] ?? null);
}
/**
 * The item's headline price and span — **always the default option's**, when it
 * has one.
 *
 * `base` is the item's own `price_cents` and its `item_scheduling.duration_min`.
 * An option with a `null` `duration_min` changes the price and leaves the span
 * alone, which is a dish's *Large*.
 */
export function headlineOf(base, options) {
    const chosen = defaultItemOption(options);
    if (chosen === null) {
        return { price_cents: base.price_cents, duration_min: base.duration_min, option_id: null };
    }
    return {
        price_cents: chosen.price_cents,
        duration_min: chosen.duration_min ?? base.duration_min,
        option_id: chosen.id,
    };
}
/** Minutes the attached add-ons add to the span. Empty is zero, which is the fleet. */
export function addOnMinutesOf(addOns) {
    return addOns.reduce((total, a) => total + a.duration_min, 0);
}
/**
 * What a booking of this item, at this option, with these add-ons, **costs** —
 * and the only arithmetic anywhere that answers it.
 *
 * B7 is untouched and is the reason this takes rows rather than numbers: every
 * input is a catalog row the server read by id, and there is no overload that
 * accepts a price. The caller on the partner side is an LLM agent and it is
 * persuadable; this function is not.
 */
export function bookingPriceCents(base, option, addOns = []) {
    const head = option?.price_cents ?? base.price_cents;
    return addOns.reduce((total, a) => total + a.price_cents, head);
}
//# sourceMappingURL=options.js.map